import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { side, sideMenuTypes } from 'config/Routes';
import Icon from 'components/commons/icon/Icon';

import * as env from 'config/environment';
import { GetUserResut, getUser } from 'config/api';

import { useRouting } from 'hooks/useRouting';
import { getScrollStorage, updateScrollStorage } from 'utils/storage';
import { PingState } from 'components/commons/Header';
import { getUserStorage } from '../utils/utils';
import { isApp, isPremium, isIosApp } from '../utils/userStatus';
import { useMigrateToMonoStorage } from '../modal/MigrateToMonoModal';
import { LineNoticeBanner } from '../home/HomeContentsInternal';

// レコード数にあわせて、アニメーション可動域を算出
const calcMaxHeight = (
  openSideSubMenu: boolean,
  length: number | undefined
): string => {
  const pxZero = '0px';
  if (!openSideSubMenu || length === undefined) return pxZero;

  const itemHeight =
    document.getElementById('id_sideMenuItem')?.offsetHeight || 0;
  return `${itemHeight * length}px`;
};

type SideMenuProps = {
  hideAnimate: boolean;
  menuHide: () => void;
  onNavLinkClick(e: React.MouseEvent, path: string, height?: number): void;
  showPing: PingState;
};

const SideMenu: React.FC<SideMenuProps> = props => {
  const location = useLocation();

  const { hideAnimate, menuHide, onNavLinkClick } = props;
  const [isOpen, setIsOpen] = useState(true);
  const [isOpenMenuBeginner, setOpenMenuBeginner] = useState(false);

  const routing = useRouting();
  const handlePremiumLp = (): void => {
    routing.pushRouting({ path: '/premiumLp' }, true);
  };

  const handleNotification = (): void => {
    // スクロール
    const scrollStorage = getScrollStorage();
    if (scrollStorage !== null) {
      // memo: セッションストレージにスクロール位置を保有している場合はスクロールさせないためにスクロール位置を削除する。
      const list = scrollStorage.filter(
        storage => storage.path !== `/notificationList/1/1/1`
      );
      updateScrollStorage([...list]);
    }

    routing.pushRouting(
      { path: `/notificationList/1/1/1` },
      true,
      window.scrollY
    );
  };

  const openCloseMenuBeginner = (): void => {
    setOpenMenuBeginner(!isOpenMenuBeginner);
  };

  const [userInfo, setUserInfo] = useState<GetUserResut | null>(null);

  useEffect(() => {
    getUser().then(ret => {
      setUserInfo(ret);
    });
  }, []);

  useEffect(() => {
    if (hideAnimate) setIsOpen(false);
  }, [hideAnimate]);

  // 閉じる要求が来た際に、アニメーションを動かす
  const onCloseRequest = (): void => {
    setIsOpen(false);
  };

  // アニメーションの終わりを待って、コンポーネントを非表示にしてもらう
  // (厳密には、コンテンツ(サブメニュー)とマスクの両方の終わりを待つべきだが、同じ時間で動かすようにしているので、コンテンツの方だけで見る)
  const onAnimated = (): void => {
    if (!isOpen) menuHide();
  };

  const animationOption =
    ' animation-200ms animation-ease-in-out animation-fill-forwards';

  const showOrHideAnimation = isOpen
    ? { content: 'animation-appear-from-right', mask: 'animation-fadeIn' }
    : { content: 'animation-disappear-to-right', mask: 'animation-fadeOut' };
  showOrHideAnimation.content += animationOption;
  showOrHideAnimation.mask += animationOption;

  const { cleanupShowedMigrateToMonoModalFlag } = useMigrateToMonoStorage();
  const user = getUserStorage();
  if (!user) {
    window.location.href = '/';
    return <div></div>;
  }

  const handleLogout = (): void => {
    localStorage.removeItem('order_setting');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('agreed');
    cleanupShowedMigrateToMonoModalFlag();
  };

  const offsetTop = document.getElementById('id_header')?.offsetHeight ?? 0;

  // ログアウト
  const sideLogoutMenu = side
    .filter(item => {
      return item?.menuType && item?.menuType === sideMenuTypes.Logout;
    })
    .map((item, i) => {
      const subMenuList = item.subMenuList ?? [];
      return (
        <div className="flex" key={item.path} onClick={onCloseRequest}>
          {subMenuList.map((subItem, ii) => {
            const isSeparator = subMenuList.length - 1 === ii ? 'hidden' : '';
            const url = subItem.path.replace('[replace]', location.pathname);
            return (
              <a
                id={subItem.id}
                href={url}
                className="flex items-center"
                key={ii}>
                {subItem.title === 'ログアウト' ? (
                  <div onClick={handleLogout}>{subItem.title}</div>
                ) : (
                  <div>{subItem.title}</div>
                )}
                <div className={`px-3 ${isSeparator}`}>｜</div>
              </a>
            );
          })}
        </div>
      );
    });

  // 資産情報の確認
  const clsPing = 'rounded-full h-2 w-2 bg-tradewell -mt-5';
  const sideMenuUserClass = 'flex items-center pb-3';
  const chevronRight = (
    <Icon name="chevron-right" className="text-2xl text-gray-500" />
  );
  const sideMenuAsepla = side
    .filter(item => {
      return (
        item?.menuType &&
        (item?.menuType === sideMenuTypes.User ||
          item?.menuType === sideMenuTypes.UserAsset)
      );
    })
    .map((item, i) => {
      if (item.menuType === sideMenuTypes.UserAsset) {
        const menuList =
          item?.subMenuList &&
          item.subMenuList.map((subItem, ii) => {
            if (
              isIosApp() &&
              (subItem.isIosPremium === false ||
                (!isPremium() && subItem.isIosPremium))
            )
              return <></>;

            if (subItem.path.startsWith('http')) {
              return (
                <a
                  id={subItem.id}
                  href={subItem.path.replace('[replace]', location.pathname)}>
                  <div className={`${sideMenuUserClass} pl-2 text-gray-300`}>
                    <div className="flex-grow">
                      {subItem.subTitle ? subItem.subTitle : subItem.title}
                    </div>
                    {chevronRight}
                  </div>
                </a>
              );
            } else if (subItem.title === 'お知らせ') {
              return (
                <div
                  className="cursor-pointer"
                  id={subItem.id}
                  onClick={handleNotification}>
                  <div className={`${sideMenuUserClass} pl-2 text-gray-300`}>
                    <div className="flex-grow flex flex-row items-center">
                      <div>
                        {subItem.subTitle ? subItem.subTitle : subItem.title}
                      </div>
                      <span
                        className={`${clsPing} z-10 ${
                          props.showPing?.shown ? '' : 'hidden'
                        }`}
                      />
                    </div>
                    {chevronRight}
                  </div>
                </div>
              );
            } else {
              if (isIosApp() && subItem.isIosPremium) {
                return <></>;
              }

              return (
                <NavLink
                  id={subItem.id}
                  to={subItem.path}
                  exact={true}
                  activeClassName=""
                  className={`${sideMenuUserClass} pl-2`}
                  onClick={(e): void => onNavLinkClick(e, subItem.path)}>
                  <div className="flex-grow flex flex-row items-center">
                    <div>
                      {subItem.subTitle ? subItem.subTitle : subItem.title}
                    </div>
                    {subItem.isNewIcon && (
                      <div className="ml-1 -mt-3">
                        <img src="/static/images/new.svg" alt="new" />
                      </div>
                    )}
                  </div>
                  {chevronRight}
                </NavLink>
              );
            }
          });

        return (
          <div key={i}>
            <div className={sideMenuUserClass}>{item.title}</div>
            {menuList}
          </div>
        );
      } else {
        if (
          isIosApp() &&
          (item.isIosPremium === false || (!isPremium() && item.isIosPremium))
        )
          return <></>;

        return (
          !(isApp && item.doNotShowOnSideMenuApp) && (
            <a
              id={item.id}
              href={isApp && item.pathApp ? item.pathApp : item.path}
              rel="noopener noreferrer"
              key={i}
              className={sideMenuUserClass}
              onClick={(): void => onCloseRequest()}>
              <div className="flex-grow">{item.title}</div>
              {chevronRight}
            </a>
          )
        );
      }
    });

  // バナー
  const sideMenuBanner = () => {
    return (
      <>
        {!isIosApp() && (
          <>
            <div className="w-full">
              <a
                id="side_dividend"
                href="/dividenddeposit?utm_source=tw_minkabu&utm_medium=hm_menu&utm_campaign=bn_haitou_202507"
                rel="noopener noreferrer">
                <img
                  src="/static/images/side_menu/dividend_banner.svg"
                  alt="配当金"
                  className="mx-auto mb-2 px-4"
                />
              </a>
            </div>
          </>
        )}
        <div className="w-full">
          <a
            id="side_questionnaire"
            href={env.questionnaireURL}
            target="_blank"
            rel="noopener noreferrer">
            <img
              src="/static/images/side_menu/questionnaire.svg"
              alt="アンケート"
              className="mx-auto mb-2 px-4"
            />
          </a>
        </div>
      </>
    );
  };

  // 金融商品メディア
  const sideMenuBaseClass = 'flex items-center py-2 pl-6 pr-3';
  const sideMenuClass = `${sideMenuBaseClass} text-white text-sm`;
  const sideMenuFinance = (
    <div>
      <div className={`${sideMenuBaseClass} text-gray-500 text-sm`}>
        金融商品メディア
      </div>
      <div className="border-b border-gray-700" />
      {side
        .filter(
          item =>
            item?.menuType && item?.menuType === sideMenuTypes.FinancialProduct
        )
        .map((item, i) => {
          return (
            <div key={i}>
              <a
                id={item.id}
                href={item.path}
                rel="noopener noreferrer"
                className={sideMenuClass}
                onClick={(): void => onCloseRequest()}>
                <div className="flex-grow">{item.title}</div>
                {chevronRight}
              </a>
              <div className="border-b border-gray-700" />
            </div>
          );
        })}
    </div>
  );

  // 初心者
  const sideMenuBeginner = side
    .filter(item => {
      return item?.menuType === sideMenuTypes.Beginner;
    })
    .map((item, i) => {
      // max-h-custom1・max-h-0の指定を止めて、maxHeightを算出
      const maxHeight = calcMaxHeight(
        isOpenMenuBeginner,
        item?.subMenuList ? item.subMenuList.length : undefined
      );
      return (
        <div key={i}>
          <div
            className={`${sideMenuClass} cursor-pointer`}
            onClick={openCloseMenuBeginner}>
            <div className="flex-grow">{item.title}</div>
            <Icon
              name={`chevron-down transform rotate-${
                isOpenMenuBeginner ? '180' : '0'
              }`}
              className="text-2xl text-gray-500 transition duration-300"
              caption="開閉"
            />
          </div>
          <div className="border-b border-gray-700" />
          <div
            className="overflow-hidden transition-all duration-500"
            style={{ maxHeight: `${maxHeight}` }}>
            {item?.subMenuList &&
              item.subMenuList.map((subItem, ii) => {
                return (
                  <div
                    key={subItem.path}
                    id="id_sideMenuItem"
                    onClick={(): void => onCloseRequest()}>
                    <a
                      id={subItem.id}
                      href={subItem.path}
                      rel="noopener noreferrer"
                      className={sideMenuClass}
                      style={{
                        backgroundColor: '#20293b',
                      }}>
                      <div className="flex-grow pl-4">{subItem.title}</div>
                      {chevronRight}
                    </a>
                    <div className="border-b border-gray-700" />
                  </div>
                );
              })}
          </div>
        </div>
      );
    });

  // サイドメニュー下部
  const sideMenuNomalClass = `${sideMenuBaseClass} text-white text-xs`;
  const sideMenuNomal = (
    <div className="mt-3">
      {side
        .filter(
          item => item?.menuType && item?.menuType === sideMenuTypes.Nomal
        )
        .map((item, i) => {
          return (
            <a
              id={item.id}
              href={item.path}
              target="_blank"
              rel="noopener noreferrer"
              key={item.path}
              className={sideMenuNomalClass}
              onClick={(): void => onCloseRequest()}>
              {item.title}
            </a>
          );
        })}
    </div>
  );

  if (userInfo === null) return <></>;
  return (
    <div className="overflow-y-auto relative h-full w-full">
      <div
        className={`fixed left-0 bg-gray-800 h-full w-full ${showOrHideAnimation.mask}`}
        onClick={onCloseRequest}
      />
      <div
        className={`fixed right-0 bg-gray-700 h-full overflow-y-auto w-64 ${showOrHideAnimation.content}`}
        style={{
          backgroundColor: '#374151',
          width: '18rem',
          height: `calc(var(--vh, 1vh) * 100 - ${offsetTop}px)`,
        }}
        onAnimationEnd={onAnimated}>
        {!isApp && !isIosApp() && (
          <div className="p-4 pb-0 text-center">
            <ul
              id="family-link"
              className="rounded-lg border border-bluegray-600 grid grid-cols-4 text-xs text-white">
              <li className="block pt-2 pb-1 border-bluegray-600 border-b border-r">
                <a
                  id="side_stock_fromIcon"
                  href="https://minkabu.jp/"
                  className="block"
                  onClick={() => onCloseRequest()}>
                  <img
                    src="https://assets.minkabu.jp/images/icon/icon_kabu.svg"
                    width="30px"
                    height="30px"
                    alt="株式"
                    className="m-auto"
                  />
                  <p className="mt-2">株式</p>
                </a>
              </li>
              <li className="block pt-2 pb-1 border-bluegray-600 border-b border-r">
                <a
                  id="side_us_stock_fromIcon"
                  href="https://us.minkabu.jp/"
                  className="block"
                  onClick={() => onCloseRequest()}>
                  <img
                    src="https://assets.minkabu.jp/images/icon/icon_us.svg"
                    width="30px"
                    height="30px"
                    alt="米国株"
                    className="m-auto"
                  />
                  <p className="mt-2">米国株</p>
                </a>
                <div className="ml-10 -mt-14">
                  <img src="/static/images/new.svg" alt="new" />
                </div>
              </li>
              <li className="block pt-2 pb-1 border-bluegray-600 border-b border-r">
                <a
                  id="side_investmentTrust_fromIcon"
                  href="https://itf.minkabu.jp/"
                  className="block"
                  onClick={() => onCloseRequest()}>
                  <img
                    src="https://assets.minkabu.jp/images/icon/icon_toushin.svg"
                    width="30px"
                    height="30px"
                    alt="投資信託"
                    className="m-auto"
                  />
                  <p className="mt-2">投資信託</p>
                </a>
              </li>
              <li className="block pt-2 pb-1 border-bluegray-600 border-b">
                <a
                  id="side_insurance"
                  href="https://ins.minkabu.jp/"
                  className="block"
                  onClick={() => onCloseRequest()}>
                  <img
                    src="https://assets.minkabu.jp/images/icon/icon_hoken.svg"
                    width="30px"
                    height="30px"
                    alt="保険"
                    className="m-auto"
                  />
                  <p className="mt-2">保険</p>
                </a>
              </li>
              <li className="block pt-2 pb-1 border-bluegray-600 border-r">
                <a
                  id="side_FX_fromIcon"
                  href="https://fx.minkabu.jp/"
                  className="block"
                  onClick={() => onCloseRequest()}>
                  <img
                    src="https://assets.minkabu.jp/images/icon/icon_fx.svg"
                    width="30px"
                    height="30px"
                    alt="FX/為替"
                    className="m-auto"
                  />
                  <p className="mt-2">FX/為替</p>
                </a>
              </li>
              <li className="block pt-2 pb-1 border-bluegray-600 border-r">
                <a
                  id="side_futures_fromIcon"
                  href="https://fu.minkabu.jp/"
                  className="block"
                  onClick={() => onCloseRequest()}>
                  <img
                    src="https://assets.minkabu.jp/images/icon/icon_sakimono.svg"
                    width="30px"
                    height="30px"
                    alt="先物"
                    className="m-auto"
                  />
                  <p className="mt-2">先物</p>
                </a>
              </li>
              <li className="block pt-2 pb-1 border-bluegray-600 border-r">
                <a
                  id="side_virtualCurrency_fromIcon"
                  href="https://cc.minkabu.jp/"
                  className="block"
                  onClick={() => onCloseRequest()}>
                  <img
                    src="https://assets.minkabu.jp/images/icon/icon_kasoutsuka.svg"
                    width="30px"
                    height="30px"
                    alt="仮想通貨"
                    className="m-auto"
                  />
                  <p className="mt-2">仮想通貨</p>
                </a>
              </li>
              <li className="block pt-2 pb-1 border-bluegray-600">
                <a
                  id="side_studyingMoney_fromIcon"
                  href="https://mag.minkabu.jp/"
                  className="block"
                  onClick={() => onCloseRequest()}>
                  <img
                    src="https://assets.minkabu.jp/images/icon/icon_study.svg"
                    width="30px"
                    height="30px"
                    alt="お金の勉強"
                    className="m-auto"
                  />
                  <p className="mt-2">お金の勉強</p>
                </a>
              </li>
            </ul>
          </div>
        )}
        <div className="p-4">
          {!isIosApp() && (
            <>
              {!isPremium() && (
                <a href={`${env.premiumLpURL}`} className="flex items-center">
                  <img
                    className="mb-2"
                    src="/static/images/side_menu/premium_banner.png"
                    alt="みんかぶプレミアム会員でできること"
                    style={{
                      borderRadius: '0.5rem',
                    }}
                  />
                </a>
              )}
            </>
          )}
          {!userInfo.LinkedLine && (
            <LineNoticeBanner {...{ className: ['', 'w-full', 'w-full'] }} />
          )}
          <div
            className="mt-2 text-white text-sm"
            style={{
              backgroundColor: '#11172a',
              borderRadius: '0.5rem',
            }}>
            <div className="p-4">
              <div className="flex justify-end">{sideLogoutMenu}</div>
              <div className="flex items-center">
                {user.ProfileImageURL ? (
                  <img
                    src={user.ProfileImageURL}
                    className="rounded-full w-10 h-10 mr-1"
                    alt="プロフィール"
                  />
                ) : (
                  <Icon
                    name="person"
                    className="text-4xl border rounded-full border-gray-700 mr-1"
                  />
                )}
                {isPremium() && (
                  <img
                    src={env.premiumLogoURL}
                    alt="プレミアム会員"
                    className="h-5 w-auto ml-1"
                  />
                )}
                <div className="text-base font-bold truncate ml-2">
                  {user.Name}
                </div>
              </div>
            </div>
            <div className="border-b border-gray-700" />
            <div className="pt-4 pl-4 pr-1">{sideMenuAsepla}</div>
          </div>
        </div>
        {sideMenuBanner()}
        {!isApp && sideMenuFinance}
        {!isApp && sideMenuBeginner}
        {sideMenuNomal}
        <div className="h-24"></div>
      </div>
    </div>
  );
};

export default SideMenu;
